import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, RAYG, RAYG_TEXT, CLASSIFICATIONS } from "../lib/api";
import { toast } from "sonner";
import { X, Warning, ArrowLeft, Plus, Flask } from "@phosphor-icons/react";
import { SimulationPanel } from "../components/SimulationPanel";

const FLAG_LABELS = {
  duplicate_step: "Duplicate step", repeated_data_capture: "Repeated data capture",
  manual_reconciliation: "Manual reconciliation", excessive_approvals: "Excessive approvals",
  unclear_accountability: "Unclear accountability", broken_handoff: "Broken hand-off",
  bottleneck: "Bottleneck", shadow_system: "Shadow system",
};

export default function WorkflowDetail() {
  const { id } = useParams();
  const [wf, setWf] = useState(null);
  const [depts, setDepts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSim, setShowSim] = useState(false);
  const [newStep, setNewStep] = useState({ name: "", department_key: "", role: "", system: "", classification: "human_led" });

  useEffect(() => {
    Promise.all([api.get(`/workflows/${id}`), api.get("/departments")]).then(([w, d]) => {
      setWf(w.data); setDepts(d.data);
      if (!newStep.department_key && d.data.length) setNewStep((s) => ({ ...s, department_key: d.data[0].key }));
    });
  }, [id]);

  if (!wf) return <div className="p-8">Loading…</div>;
  const deptByKey = Object.fromEntries(depts.map((d) => [d.key, d]));
  const lanes = wf.departments.map((dk) => ({ key: dk, dept: deptByKey[dk], steps: wf.steps }));

  const updateStep = async (stepId, updates) => {
    const { data } = await api.put(`/workflows/${wf.id}/steps/${stepId}`, updates);
    setWf(data);
    setSelected(data.steps.find((s) => s.id === stepId));
    toast.success("Step updated");
  };

  const addStep = async (e) => {
    e.preventDefault();
    const { data } = await api.post(`/workflows/${wf.id}/steps`, newStep);
    setWf(data); setShowAdd(false);
    setNewStep({ ...newStep, name: "", role: "", system: "" });
    toast.success("Step added");
  };

  return (
    <div className="min-h-screen pb-16" data-testid="workflow-detail-page">
      <div className="px-8 pt-8 pb-4">
        <Link to="/workflows" className="flex items-center gap-1 text-xs text-black/50 hover:text-black transition-colors mb-2" data-testid="back-to-workflows">
          <ArrowLeft size={12} /> Workflows
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{wf.name}</h1>
            <p className="text-sm text-black/55 mt-1 max-w-2xl"><b>Trigger:</b> {wf.trigger} · <b>Outcome:</b> {wf.outcome}</p>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-sm font-mono px-2.5 py-1.5 rounded-sm" style={{ background: RAYG(wf.health), color: RAYG_TEXT(wf.health) }}>health {wf.health}</span>
            <button onClick={() => setShowSim(true)} data-testid="open-simulation-btn"
              className="flex items-center gap-1 text-xs bg-[#D4AF37] text-[#0A0A0A] rounded-sm px-3 py-2 font-semibold hover:bg-[#e0c258] transition-colors"><Flask size={13} /> Simulate</button>
            <button onClick={() => setShowAdd(!showAdd)} data-testid="add-step-btn"
              className="flex items-center gap-1 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-3 py-2 font-semibold hover:bg-black/85 transition-colors"><Plus size={13} /> Add step</button>
          </div>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          {Object.entries(wf.flag_summary).map(([f, c]) => (
            <span key={f} className="flex items-center gap-1 text-[11px] bg-red-50 text-red-700 border border-red-200 rounded-full px-2.5 py-1" data-testid={`flag-summary-${f}`}>
              <Warning size={11} weight="fill" /> {FLAG_LABELS[f] || f} ×{c}
            </span>
          ))}
        </div>
      </div>

      {showAdd && (
        <form onSubmit={addStep} className="mx-8 mb-4 bg-white border border-[#D4AF37]/60 rounded-sm p-4 flex flex-wrap gap-2 items-end fade-up" data-testid="add-step-form">
          <input required placeholder="Step name" value={newStep.name} onChange={(e) => setNewStep({ ...newStep, name: e.target.value })} data-testid="step-name-input"
            className="border border-black/15 rounded-sm px-3 py-2 text-sm flex-1 min-w-[180px] outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          <select value={newStep.department_key} onChange={(e) => setNewStep({ ...newStep, department_key: e.target.value })} data-testid="step-dept-select"
            className="border border-black/15 rounded-sm px-2 py-2 text-sm bg-white">
            {depts.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          <input placeholder="Role" value={newStep.role} onChange={(e) => setNewStep({ ...newStep, role: e.target.value })}
            className="border border-black/15 rounded-sm px-3 py-2 text-sm w-36 outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          <input placeholder="System" value={newStep.system} onChange={(e) => setNewStep({ ...newStep, system: e.target.value })}
            className="border border-black/15 rounded-sm px-3 py-2 text-sm w-36 outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          <button type="submit" data-testid="step-submit-btn" className="text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2.5 font-semibold">Add</button>
        </form>
      )}

      <div className="mx-8 border border-black/10 rounded-sm bg-white overflow-x-auto" data-testid="swimlane-canvas">
        <div style={{ minWidth: Math.max(wf.steps.length * 168 + 190, 800) }}>
          {lanes.map(({ key, dept }) => (
            <div key={key} className="flex border-b border-black/8 last:border-0" data-testid={`swimlane-${key}`}>
              <div className="w-[180px] shrink-0 px-3 py-4 text-white text-[12px] font-display font-bold sticky left-0 z-10 flex items-center" style={{ background: dept?.color || "#666" }}>
                {dept?.name || key}
              </div>
              <div className="flex items-center py-4 relative">
                {wf.steps.map((s, i) => (
                  <div key={s.id} className="w-[168px] shrink-0 flex items-center justify-center relative">
                    {s.department_key === key ? (
                      <button onClick={() => setSelected(s)} data-testid={`step-node-${s.order}`}
                        className={`chip relative z-10 w-[150px] text-left border-2 bg-white px-2.5 py-2 text-[11px] leading-snug ${s.type === "decision" ? "rotate-0" : ""}`}
                        style={{ borderColor: dept?.color, borderRadius: s.type === "decision" ? "14px" : s.type === "start" || s.type === "end" ? "999px" : "4px" }}>
                        <span className="font-semibold block">{i + 1}. {s.name}</span>
                        <span className="text-black/50">{s.role}</span>
                        <div className="flex gap-1 mt-1 items-center flex-wrap">
                          <span className="text-[9px] px-1 py-0.5 rounded-sm text-white" style={{ background: CLASSIFICATIONS[s.classification]?.color }}>{CLASSIFICATIONS[s.classification]?.label}</span>
                          {s.flags.length > 0 && <Warning size={11} weight="fill" className="text-red-500" />}
                        </div>
                      </button>
                    ) : (
                      <div className="w-full h-px bg-black/10" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-y-0 right-0 w-[400px] bg-white border-l border-black/10 shadow-2xl z-40 overflow-y-auto fade-up" data-testid="step-detail-panel">
          <div className="p-4 bg-[#0A0A0A] text-white sticky top-0 flex justify-between items-start">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Step {selected.order + 1} · {selected.type}</p>
              <h3 className="font-display font-bold text-lg tracking-tight">{selected.name}</h3>
            </div>
            <button onClick={() => setSelected(null)} data-testid="close-step-panel" className="hover:text-[#D4AF37] transition-colors"><X size={18} /></button>
          </div>
          <div className="p-4 space-y-4 text-[13px]">
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Department</p><p className="font-medium">{deptByKey[selected.department_key]?.name}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Role</p><p className="font-medium">{selected.role}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">System</p><p className="font-medium">{selected.system}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Effort / wait</p><p className="font-medium">{selected.effort_mins}m / {selected.wait_mins}m</p></div>
            </div>
            {["data_objects", "controls", "risks"].map((k) => (
              <div key={k}>
                <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1">{k.replace("_", " ")}</p>
                {selected[k]?.length ? selected[k].map((v) => <span key={v} className="inline-block text-[11px] bg-black/5 rounded-sm px-2 py-0.5 mr-1 mb-1">{v}</span>) : <p className="text-black/40 text-[12px]">None linked</p>}
              </div>
            ))}
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1.5">Work-redesign classification</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(CLASSIFICATIONS).map(([k, c]) => (
                  <button key={k} onClick={() => updateStep(selected.id, { classification: k })} data-testid={`classify-${k}`}
                    className="text-[11px] px-2 py-1 rounded-sm border transition-colors"
                    style={selected.classification === k ? { background: c.color, color: "#fff", borderColor: c.color } : { borderColor: "#00000022" }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-black/50 mb-1.5">Issue flags</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(FLAG_LABELS).map(([k, l]) => {
                  const active = selected.flags.includes(k);
                  return (
                    <button key={k} onClick={() => updateStep(selected.id, { flags: active ? selected.flags.filter((f) => f !== k) : [...selected.flags, k] })}
                      data-testid={`flag-toggle-${k}`}
                      className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${active ? "bg-red-500 text-white border-red-500" : "border-black/15 hover:border-red-300"}`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {showSim && <SimulationPanel wf={wf} depts={depts} onClose={() => setShowSim(false)} />}
    </div>
  );
}
